import React, { useCallback, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { useAppState } from '../../data';
import CookieDetails from './cookie-details/CookieDetails';
import VendorsList from './vendors-list/VendorsList';
import ReactDOMServer from 'react-dom/server';

export interface CookiesDialogProps {
  handleClose?(): void;
  open: boolean;
}

const FadeIn = keyframes`
  0% {
    opacity: 0
  }

  100% {
    opacity: 1
  }
`;

const Dialog = styled.div<{ open: boolean }>`
  display: ${props => props.open ? 'block' : 'none'};
  position: fixed;
  width: 730px;
  max-width: 730px;
  min-width: 350px;
  height: 580px;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  font-size: 16px;
  z-index: 2147483645;
  border-radius: 2px;
  background-color: #fff;
  box-shadow: 0 2px 4px 0 rgba(0,0,0,0), 0 7px 14px 0 rgba(50,50,93,.1);

  animation-name: ${FadeIn};
  animation-duration: 400ms;
  animation-timing-function: ease-in-out;

  @media only screen and (max-width: 896px) and (min-width: 320px) and (orientation: landscape) {
    height: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const DialogHeader = styled.div`
  height: auto;
  padding: 20px;
  display: flex;
  vertical-align: middle;
  width: 100%;
  border-bottom: 1px solid #d8d8d8;
`;

const DialogContent = styled.div`
  height: calc(100% - 194px);
  overflow-y: auto;

  @media (max-width: 640px) {
    height: calc(100% - 305px);
  }

  ::after {
    content: " ";
    display: table;
    clear: both;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 30px;
  bottom: 0;
  border-top: 1px solid #d8d8d8;

  @media only screen and (max-width: 896px) and (min-width: 320px) and (orientation: landscape) {
    padding: 20px;
    width: 100%;
  }

  @media (max-width: 640px) {
    padding: 15px;
    margin: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  padding-right: 20px;
`;

const Logo = styled.div`
  height: 60px;
  width: 180px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;

  @media (max-width: 400px) {
    width: 120px;
  }
`;

const TitleContainer = styled.div`
  width: calc(100% - 190px);
  margin-left: 10px;

  @media (max-width: 400px) {
    width: calc(100% - 130px);
  }
`;

const PoweredContainer = styled.div`
  text-align: center;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  width: 180px;
  height: 25px;
  position: relative;
  top: 10px;
  font-size: .9em;
  display: flex;
  align-items: center;
  flex-basis: 35%;

  @media (max-width: 767px) {
    width: 100%;
  }

  @media only screen and (max-width: 896px) and (min-width: 320px) and (orientation: landscape) {
    width: auto;
  }
  
  @media (max-width: 640px) {
    order: 2;
    justify-content: center;
    margin: 20px auto;
  }
`;

const PoweredLink = styled.a<{dataURI: string}>`
  background-image: url('${props => props.dataURI}');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  height: 25px;
  width: 100px;
  display: inline-block;
`;

const ButtonGroup = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
  flex: 1;
  
  @media (max-width: 640px) {
    order: 1;
    flex-direction: column;
  }
`;

const Button = styled.button<{ textColor: string, backgroundColor: string }>`
  background-color: ${props => props.backgroundColor};
  color: ${props => props.textColor};
  min-width: 125px;
  height: auto;
  white-space: normal;
  word-break: break-word;
  word-wrap: break-word;
  padding: 12px 10px;
  line-height: 1.2;display: inline-block;
  text-align: center;
  font-size: 0.9em;
  font-weight: 400;
  letter-spacing: 0.01em;
  text-decoration: none;
  border-radius: 2px;
  border: 1px solid ${props => props.backgroundColor};
  cursor: pointer;
  box-sizing: border-box;
  margin: auto 15px;
  
  @media (max-width: 640px) {
    :not(:last-child) {
      margin-bottom: 15px;
    }
  }
`;

const Title = styled.h2`
  display: block;
  font-size: 1.5em;
  margin: .83em 0;
  font-weight: 600;
`;

const ListRow = styled.div`
  max-width: none;
  display: block;
  margin: 0;
  flex-direction: initial;
  width: 100%;
`;

const CookieList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style: circle inside;

  ::after {
    content: "";
    display: table;
    clear: both;
  }
`;

const CookieListItem = styled.li`
  margin: 0;
  list-style: none;
`;

function SYDLELogo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="12904 -333.677 140 11.051">
      <path style={{fill: '#000'}} d="M12920.553-332.043h-13.772a1.6 1.6 0 0 0-1.273 1.609 1.527 1.527 0 0 0 1.24 1.541h11.661a3.159 3.159 0 0 1 2.714 3.183 3.04 3.04 0 0 1-.67 1.944 3.124 3.124 0 0 1-2.044 1.139h-13.873a.823.823 0 0 1-.536-.8.81.81 0 0 1 .536-.771h13.806a1.469 1.469 0 0 0 1.24-1.508 1.572 1.572 0 0 0-1.24-1.608h-11.594a3.251 3.251 0 0 1-2.078-1.11 3.2 3.2 0 0 1-.67-1.977 3.247 3.247 0 0 1 .7-2.077 3.05 3.05 0 0 1 2.115-1.106h13.6a.736.736 0 0 1 .67.771.767.767 0 0 1-.532.77zm30.929-1.541a.759.759 0 0 1 .369.637.93.93 0 0 1-.57.838c-.6.335-3.049 1.809-7.271 4.356v4.354a.716.716 0 0 1-.771.67.784.784 0 0 1-.771-.57v-4.455c-.034 0-.5-.3-1.374-.838-.268-.134-.6-.335-1.039-.6-.168-.1-.469-.3-.9-.57 0 0-.268-.168-.771-.469l-2.35-1.41c-.268-.134-.57-.3-.838-.469-.369-.268-.57-.5-.57-.7a.69.69 0 0 1 .3-.536.779.779 0 0 1 .9-.2c.536.369 3.016 1.876 7.472 4.524.235-.134.737-.4 1.441-.838a436.532 436.532 0 0 1 6.065-3.652.512.512 0 0 1 .678-.072zm31.164 5.462a6.281 6.281 0 0 1-.838 2.915 4.612 4.612 0 0 1-1.575 1.675 4.932 4.932 0 0 1-1.977.8q-.452.1-.3.1h-11.83c-.134 0-.268-.1-.469-.268-.2-.2-.3-.335-.3-.469v-9.478a.686.686 0 0 1 .3-.5.9.9 0 0 1 .536-.237h11.628a4.343 4.343 0 0 1 2.245.871 4.568 4.568 0 0 1 1.709 1.709c.034.067.134.2.235.369a4.518 4.518 0 0 1 .636 2.513zm-1.575 0a3.987 3.987 0 0 0-.6-2.145 3.438 3.438 0 0 0-2.044-1.575 6.856 6.856 0 0 0-.737-.2h-10.76v7.84h10.756a3.876 3.876 0 0 0 2.748-1.709 4.248 4.248 0 0 0 .637-2.211zm31.632 5.495h-15.883c-.134 0-.268-.1-.469-.268-.2-.2-.3-.335-.3-.469v-9.617a.784.784 0 0 1 .771-.57.758.758 0 0 1 .771.67v8.712h15.076a.845.845 0 0 1 .57.771c.034.302-.134.57-.536.771zm30.727 0h-15.883c-.134 0-.268-.1-.469-.268-.2-.2-.3-.335-.3-.469v-9.483a.686.686 0 0 1 .3-.5.905.905 0 0 1 .57-.235h15.682a.736.736 0 0 1 .67.771.784.784 0 0 1-.57.771h-15.079v3.15h15.079a.845.845 0 0 1 .57.771.922.922 0 0 1-.57.8h-15.079v3.15h15.079a.845.845 0 0 1 .57.771.918.918 0 0 1-.57.771z"/>
    </svg>
  );
}

export default function CookiesDialog({ open }: CookiesDialogProps) {
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const { showVendors, appConfig, setShowApp, getTranslation } = useAppState();
  const cookiesCategories = appConfig.cookiesConfigs.map(conf => ({
    title: conf.type,
    description: conf.description,
    required: !conf.optional
  }));

  const handleAccept = useCallback(() => {
    setShowApp(false);
    localStorage.setItem('sydle_one_lgpd_closed', new Date().toISOString());
  }, [setShowApp]);
  
  const handleSave = useCallback(() => {
    const configs: any = {};
    document.querySelectorAll<HTMLInputElement>('input[id^=sb-lgpd-cd-]').forEach(input => {
      const idx = input.id.replace('sb-lgpd-cd-', '');
      configs[idx] = input.checked;
    });
    setShowApp(false);
    localStorage.setItem('sydle_one_lgpd_closed', new Date().toISOString());
    localStorage.setItem('sydle_one_lgpd_config', JSON.stringify(configs));
  }, [setShowApp]);

  const svgString = ReactDOMServer.renderToStaticMarkup(<SYDLELogo />);
  const dataUri = `data:image/svg+xml;base64,${btoa(svgString)}`;
  
  return (
    <Dialog as="ldiv" open={open}>
      <DialogHeader as="ldiv">
        {appConfig.UIConfig.headerImage && 
        <LogoContainer as="ldiv">
          <Logo as="ldiv" style={{ backgroundImage: `url(${window.SYDLE_LGPD_ENV?.REACT_APP_BASE_URL}/api/1/${window.SYDLE_LGPD_ENV?.REACT_APP_APPLICATION}/_classId/5f63788bbce7a3412b7cf363/getHeader/${appConfig._id})` }} />
        </LogoContainer>
        }
        <TitleContainer as="ldiv">
          <Title as="ldiv">{appConfig.UIConfig.dialogTitle}</Title>
        </TitleContainer>
      </DialogHeader>
        <DialogContent as="ldiv">
          {showVendors < 0 ?
            <ListRow as="ldiv">
              <ldiv onClick={() => setActiveIndex(-1)}>
                <CookieDetails active={activeIndex === -1} title={appConfig.UIConfig.privacySectionTitle} description={appConfig.UIConfig.privacySectionText} hideCookies={true}/>
              </ldiv>
              <CookieList as="ldiv">
                {cookiesCategories.map((cc, index) => (
                  <CookieListItem as="ldiv" key={index}>
                    <CookieDetails onClick={() => setActiveIndex(index)} active={index === activeIndex} title={cc.title} description={cc.description} index={index} required={cc.required}/>
                  </CookieListItem>
                ))}
              </CookieList>
            </ListRow>
          :
            <VendorsList />
          }
        </DialogContent>
      <DialogFooter as="ldiv">
        <PoweredContainer as="ldiv">
          {getTranslation('COOKIES_DIALOG_POWERED_BY')} &nbsp;<PoweredLink dataURI={dataUri} href="https://www.sydle.com/" target="_blank"/>
        </PoweredContainer>
        <ButtonGroup as="ldiv">
          <Button as="ldiv" textColor={appConfig.UIConfig.buttonFontColor} backgroundColor={appConfig.UIConfig.buttonBackgroundColor} onClick={handleSave}>{appConfig.UIConfig.saveSettingsButtonText}</Button>
          <Button as="ldiv" textColor={appConfig.UIConfig.buttonFontColor} backgroundColor={appConfig.UIConfig.buttonBackgroundColor} onClick={handleAccept}>{appConfig.UIConfig.acceptButtonText}</Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
}